import { useAtomValue } from "jotai";
import {
  passengersAtom,
  promoCodeAtom,
  selectedDateAtom,
  selectedPackageAtom,
  selectedSeasonAtom,
  currentStepAtom,
  campaignValidationAtom,
  campaignApplicationResultAtom,
} from "./store";
import { formatDate, renderFromToTimeString } from "../../utils/date";
import { textStyles } from "../../styles/typography";
import { ccn } from "../../styles/utils";
import { useListingQuery, usePricingCalculateQuery } from "../../api/hooks";
import { PricingLineItem, PricingMode } from "@repo/rq-api-public";
import { usePromoCodeDiscountQuery, useBookingStartDateTime } from "../hooks";
import { formatCurrency } from "../../utils/currency";

function LineItem({
  item,
  discount,
  currency,
  locale,
}: {
  item: PricingLineItem;
  discount?: number;
  currency?: string;
  locale?: string;
}) {
  return (
    <li key={item.name} className="flex justify-between">
      <div className={ccn(textStyles.body1, "text-blue-dark60")}>
        {item.name}
      </div>
      <div className={ccn(textStyles.sub1, "text-blue-dark")}>
        {discount !== undefined && discount > 0 && (
          <span className="line-through mr-1 text-blue-dark60">
            {formatCurrency(item.price ?? 0, currency, locale)}
          </span>
        )}
        {formatCurrency((item.price ?? 0) - (discount ?? 0), currency, locale)}
      </div>
    </li>
  );
}

export default function BookingSummary() {
  const passengers = useAtomValue(passengersAtom);
  const selectedDate = useAtomValue(selectedDateAtom);
  const selectedPackage = useAtomValue(selectedPackageAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const promoCode = useAtomValue(promoCodeAtom);
  const currentStep = useAtomValue(currentStepAtom);
  const campaignValidation = useAtomValue(campaignValidationAtom);
  const campaignApplicationResult = useAtomValue(campaignApplicationResultAtom);
  const startDateTime = useBookingStartDateTime();

  const { data: listing } = useListingQuery();
  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;

  // Extract currency and locale from listing (will be provided by backend)
  const currency = listing?.currency || "USD";
  const locale = listing?.culture || "en-US";

  const { data: calculation } = usePricingCalculateQuery({
    promoCode: (!promoCode?.isLoading && promoCode?.submittedCode) || undefined,
    promoCodeStartDateTime: startDateTime,
  });

  const { data: promoResponse } = usePromoCodeDiscountQuery();

  // Only show promo code discount, not campaign discount in booking summary
  const appliedDiscount = promoResponse?.discountAmount;

  const [rentalLineItem, ...allLineItems] = calculation?.items ?? [];

  // Filter and rename Campaign Code Discount line item based on step and validity
  const lineItems = (() => {
    if (currentStep === 3) {
      // Step 3: Show Campaign Code Discount only if it's valid and successfully applied
      const isCampaignValid =
        campaignValidation.isValid === true &&
        campaignApplicationResult === "success";

      return allLineItems
        .filter((item) => {
          // If it's Campaign Code Discount and not valid, filter it out
          if (item.name === "Campaign Code Discount" && !isCampaignValid) {
            return false;
          }
          return true;
        })
        .map((item) => {
          if (item.name === "Campaign Code Discount") {
            // Create a new PricingLineItem with updated name
            const updatedItem = new PricingLineItem(item);
            updatedItem.name = "Discount";
            return updatedItem;
          }
          return item;
        });
    } else {
      // Step 1 and 2: Show all items without modification (campaign code won't be in calculation)
      return allLineItems;
    }
  })();

  if (!calculation || !listing) {
    return null;
  }

  if (isCalendarMode && !selectedSeason) {
    return null;
  }

  if (!isCalendarMode && !selectedPackage) {
    return null;
  }

  return (
    <div>
      <h2
        className={ccn(
          textStyles.h2,
          "text-blue-dark text-center md:mb-10 mb-4",
        )}
      >
        Booking Summary
      </h2>
      <ul className="flex flex-col gap-3">
        <li className="flex justify-between">
          <div className={ccn(textStyles.body1, "text-blue-dark60")}>
            Charter
          </div>
          <div className={ccn(textStyles.sub1, "text-blue-dark text-right")}>
            {listing.title}
            <br /> {passengers} Passengers
          </div>
        </li>

        <li className="flex justify-between text-right border-b border-grey-primary pb-3">
          <div className={ccn(textStyles.body1, "text-blue-dark60")}>Date</div>
          <div className={ccn(textStyles.sub1, "text-blue-dark")}>
            {formatDate(selectedDate)} <br />
            {isCalendarMode ? (
              <>8 Hrs</>
            ) : (
              renderFromToTimeString(
                selectedPackage!.startTime,
                selectedPackage!.duration,
              )
            )}
          </div>
        </li>

        {rentalLineItem && (
          <LineItem
            item={rentalLineItem}
            discount={appliedDiscount}
            currency={currency}
            locale={locale}
          />
        )}

        {/* TODO: (future) adjust BE to have an enum to filter items by */}
        {/* {promoResponse && (
          <li className="flex justify-between">
            <div className={ccn(textStyles.body1, "text-green-primary")}>
              Promo Code Discount
            </div>
            <div className={ccn(textStyles.sub1, "text-green-primary")}>
              <span className="text-blue-light">
                -{formatCurrency(promoResponse.discountAmount ?? 0, currency, locale)}
              </span>
            </div>
          </li>
        )} */}

        {lineItems.map((item: PricingLineItem) => (
          <LineItem
            key={item.name}
            item={item}
            currency={currency}
            locale={locale}
          />
        ))}

        <li className="flex justify-between border-t border-grey-primary pt-3">
          <div className={ccn(textStyles.h2, "text-blue-dark60")}>
            Total Amount
          </div>
          <div className={ccn(textStyles.h2, "text-blue-light")}>
            {formatCurrency(calculation.totalPrice ?? 0, currency, locale)}
          </div>
        </li>
      </ul>
    </div>
  );
}
